import React from "react";
import { Link } from "react-router-dom";
import StandalonePageLayout from "../layout/StandalonePageLayout";
import Reveal from "../../public/Reveal";
import StampButton from "../../public/StampButton";
import { PageOpening, Rule, SectionTitle, Prose } from "../../public/Journal";

// One index line: label, dotted leader, mono path. Description beneath.
function IndexLine({ link }) {
    const inner = (
        <>
            <span className="flex items-baseline gap-3">
                <span className="font-news text-[20px] leading-tight">
                    <span className="link-underline">{link.label}</span>
                </span>
                <span aria-hidden="true" className="hidden min-w-[24px] flex-1 translate-y-[-4px] border-b border-dotted border-ink/30 sm:block dark:border-paper/30" />
                <span className="hidden font-plex-mono text-[11.5px] text-ink/50 sm:inline dark:text-paper/50">{link.path || "—"}</span>
            </span>
            <span className="mt-1 block break-all font-plex-mono text-[11px] text-ink/45 sm:hidden dark:text-paper/45">{link.path || "—"}</span>
            <span className="mt-1 block font-plex text-[14px] leading-relaxed text-ink/60 dark:text-paper/60">{link.description}</span>
        </>
    );
    return (
        <li className="border-t border-ink/10 py-3.5 dark:border-paper/10">
            <Link to={link.path} className="group block">{inner}</Link>
        </li>
    );
}

function Sitemap() {
    const sections = [
        {
            title: "Public Pages",
            links: [
                { path: "/", label: "Home", description: "Main landing page" },
                { path: "/login", label: "Login", description: "Sign in to your account" },
                { path: "/signup", label: "Sign Up", description: "Create a new account" },
                { path: "/forgot-password", label: "Forgot Password", description: "Reset your password" },
            ]
        },
        {
            title: "Dashboard",
            links: [
                { path: "/dashboard", label: "Overview", description: "Dashboard home" },
                { path: "", label: "Chat", description: "AI-powered chat interface" },
                { path: "/dashboard/chats", label: "Chat History", description: "View past conversations" },
                { path: "/dashboard/studio", label: "Studio", description: "AI image generation" },
                { path: "/dashboard/profitability-index", label: "Profitability Index", description: "Property risk at the intersection of profitability and affordability" },
                // { path: "/dashboard/data-extraction", label: "Data Extraction", description: "Extract data from documents" },
                { path: "/dashboard/documents", label: "Documents", description: "Manage your documents" },
            ]
        },
        {
            title: "User Account",
            links: [
                { path: "/dashboard/profile", label: "Profile", description: "View and edit your profile" },
                // { path: "/dashboard/password-change", label: "Change Password", description: "Update your password" },
                { path: "/dashboard/settings", label: "Settings", description: "Account settings" },
            ]
        },
        // {
        //     title: "Administration",
        //     links: [
        //         { path: "/dashboard/admin", label: "Admin Dashboard", description: "Admin panel (admin only)" },
        //     ]
        // },
        {
            title: "Information & Support",
            links: [
                { path: "/terms", label: "Terms & Conditions", description: "Legal terms and privacy policy" },
                { path: "/status", label: "System Status", description: "Service status and uptime" },
                { path: "/accessibility", label: "Accessibility", description: "Accessibility statement" },
                { path: "/sitemap", label: "Sitemap", description: "Site navigation" },
                { path: "/resources", label: "Resources", description: "Documentation and guides" },
            ]
        }
    ];

    return (
        <StandalonePageLayout>
            <PageOpening
                section="§ I"
                label="Index"
                title="Every page in JPRO, in one place."
                intro="Navigate through all available pages and features of JPRO. Find what you need quickly with our organized site structure."
            />

            <div className="relative pb-6">
                <Rule strong className="absolute inset-x-0 top-0" />
                <div className="grid gap-x-16 pt-12 md:grid-cols-2">
                    {sections.map((section, sectionIndex) => (
                        <Reveal key={section.title} delay={(sectionIndex % 2) * 80} className="mb-14 break-inside-avoid">
                            <h2 className="flex items-baseline gap-3 pb-3">
                                <span className="font-plex-mono text-[11.5px] uppercase tracking-[0.2em] text-stamp">
                                    {String(sectionIndex + 1).padStart(2, "0")}
                                </span>
                                <span className="font-plex-mono text-[11.5px] uppercase tracking-[0.2em] text-ink/70 dark:text-paper/70">
                                    {section.title}
                                </span>
                            </h2>
                            <ul>
                                {section.links.map((link) => (
                                    <IndexLine key={link.label} link={link} />
                                ))}
                            </ul>
                        </Reveal>
                    ))}
                </div>
            </div>

            {/* Additional Info */}
            <section className="relative py-16 sm:py-20">
                <Rule strong className="absolute inset-x-0 top-0" />
                <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-end">
                    <div>
                        <SectionTitle>Need help?</SectionTitle>
                        <Prose className="mt-4">
                            <p>If you can&rsquo;t find what you&rsquo;re looking for, check out our resources page or contact support.</p>
                        </Prose>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
                        <Link to="/resources" className="link-underline font-plex text-[15px] text-ink/80 hover:text-ink dark:text-paper/80 dark:hover:text-paper">
                            View resources
                        </Link>
                        <StampButton to="/dashboard">Go to dashboard</StampButton>
                    </div>
                </Reveal>
            </section>
        </StandalonePageLayout>
    );
}

export default Sitemap;
